import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAuth } from "shared/contexts/Auth/AuthContext";
import { ILoja } from "shared/interfaces/ILoja";
import { IConta } from "shared/interfaces/IConta";
import { buscarLojasPorUsuario } from "shared/services/api/lojas.api";
import { buscarDadosBancarios } from "shared/services/api/dadosBancarios.api";

interface ILojaContextData {
  loja: ILoja | null;
  dadosBancarios: IConta | null;
  loading: boolean;
  refreshLoja: () => Promise<void>;
  limparLoja: () => void;
  possuiLoja: boolean;
  possuiEndereco: boolean;
  possuiDadosBancarios: boolean;
  kycValidado: boolean;
  podeCadastrarProdutos: boolean;
}

const LojaContext = createContext<ILojaContextData>({} as ILojaContextData);

interface Props {
  children: ReactNode;
}

export const LojaProvider = ({ children }: Props) => {
  const { signed } = useAuth();

  const [loja, setLoja] = useState<ILoja | null>(null);

  const [dadosBancarios, setDadosBancarios] = useState<IConta | null>(null);

  const [loading, setLoading] = useState(false);

  const carregandoRef = useRef(false);

  const limparLoja = useCallback(() => {
    setLoja(null);
    setDadosBancarios(null);
  }, []);

  const refreshLoja = useCallback(async () => {
    if (carregandoRef.current) {
      return;
    }

    carregandoRef.current = true;
    setLoading(true);

    try {
      const lojas = await buscarLojasPorUsuario();

      const lojaAtual =
        lojas.find((loja) => loja.status === "Ativo") ??
        lojas.find((loja) => loja.status !== "Inativo") ??
        lojas[0];

      if (!lojaAtual) {
        limparLoja();
        return;
      }

      setLoja(lojaAtual);

      try {
        const conta = await buscarDadosBancarios(lojaAtual.id);

        setDadosBancarios(conta);
      } catch (error) {
        setDadosBancarios(null);
      }
    } catch (error) {
      limparLoja();
    } finally {
      carregandoRef.current = false;
      setLoading(false);
    }
  }, [limparLoja]);

  useEffect(() => {
    if (!signed) {
      limparLoja();
      return;
    }

    refreshLoja();
  }, [signed, refreshLoja, limparLoja]);

  const possuiLoja = !!loja;

  const possuiEndereco = !!loja?.endereco;

  const possuiDadosBancarios = !!dadosBancarios;

  const kycStatus = dadosBancarios?.kycStatus?.toUpperCase();

  const gatewayStatus = dadosBancarios?.gatewayStatus?.toUpperCase();

  const kycValidado =
    kycStatus === "APPROVED" ||
    kycStatus === "VALIDATED" ||
    kycStatus === "VERIFIED" ||
    kycStatus === "VALID" ||
    gatewayStatus === "ACTIVE" ||
    gatewayStatus === "APPROVED" ||
    gatewayStatus === "VALIDATED" ||
    gatewayStatus === "VERIFIED" ||
    gatewayStatus === "VALID";

  const podeCadastrarProdutos =
    possuiLoja && kycValidado && loja.status === "Ativo";

  const value = useMemo(
    () => ({
      loja,
      dadosBancarios,
      loading,
      refreshLoja,
      limparLoja,
      possuiLoja,
      possuiEndereco,
      possuiDadosBancarios,
      kycValidado,
      podeCadastrarProdutos,
    }),
    [
      loja,
      dadosBancarios,
      loading,
      refreshLoja,
      limparLoja,
      possuiLoja,
      possuiEndereco,
      possuiDadosBancarios,
      kycValidado,
      podeCadastrarProdutos,
    ],
  );

  return <LojaContext.Provider value={value}>{children}</LojaContext.Provider>;
};

export const useLoja = () => {
  const context = useContext(LojaContext);

  if (!context) {
    throw new Error("useLoja deve ser utilizado dentro de um LojaProvider.");
  }

  return context;
};
